import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { showErrorToast } from "#app/notifications";
import { makePartComplexityCsv } from "#modules/parts/domain/makePartComplexityCsv";
import type { PartComplexity } from "shared";

export const usePartComplexityCsvDownload = (
  complexity: PartComplexity | null | undefined,
  partName: string,
) => {
  const { t } = useTranslation();
  const canDownload = !!(
    complexity?.measures?.length ||
    complexity?.gdts?.length ||
    complexity?.roughnesses?.length
  );

  const downloadCsv = useCallback(() => {
    if (!complexity || !canDownload) {
      showErrorToast({
        title: t("part.analysis2D.downloadError"),
        message: t("part.analysis2D.downloadErrorMessage"),
      });
      return;
    }

    const csvData = makePartComplexityCsv(complexity);
    const csvContent = csvData.join("\r\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `${partName}-cotes.csv`;
    link.style.display = "none";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    window.URL.revokeObjectURL(url);
  }, [complexity, canDownload, partName, t]);

  return { downloadCsv, canDownload };
};
